/**
 * HEAREN V1.2 — FAVORITES & PLAYLISTS (Unified scrollable screen)
 * 
 * Structure:
 *   [Section 1] Playlists — horizontal cards + "New playlist"
 *   [Section 2] Favorites — track list with Play + heart + add-to-playlist
 *   [Sub-view]  Playlist detail — when tapping a playlist card
 *   [Modal]     CreatePlaylistModal — simple name input
 *   [Modal]     AddToPlaylistModal — bottom sheet to pick target playlist
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, ListMusic, Play, Music, Loader2, Plus, ChevronLeft, Check, X, Trash2,
} from 'lucide-react';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { getThemeLabel } from '@/config/themes';

/* ──────────────────────────────────────────────
   CREATE PLAYLIST MODAL
   ────────────────────────────────────────────── */
const CreatePlaylistModal = ({ open, onClose, onCreate, t }) => {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  if (!open) return null;

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed || creating) return;
    setCreating(true);
    try {
      await onCreate(trimmed);
      setName('');
      onClose();
    } catch (e) {
      console.warn('[CreatePlaylist] error:', e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="create-playlist-modal">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      {/* sheet */}
      <div
        className="relative w-full max-w-md rounded-t-2xl px-5 pt-5 pb-24 animate-slide-up"
        style={{ background: '#0f0a1e', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white text-base font-medium">{t?.createNewPlaylist || 'Nouvelle playlist'}</h3>
          <button onClick={onClose} className="text-white/40 p-1" data-testid="create-playlist-close">
            <X size={18} />
          </button>
        </div>

        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder={t?.enterPlaylistName || 'Nom de la playlist'}
          className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="create-playlist-input"
        />

        <button
          onClick={handleCreate}
          disabled={!name.trim() || creating}
          className="w-full mt-4 py-3 rounded-xl text-sm font-medium transition-all active:scale-[0.97]"
          style={{
            background: name.trim() ? 'rgba(139,92,246,0.35)' : 'rgba(255,255,255,0.04)',
            color: name.trim() ? '#e9d5ff' : 'rgba(255,255,255,0.25)',
            border: '1px solid rgba(139,92,246,0.2)',
          }}
          data-testid="create-playlist-confirm"
        >
          {creating ? <Loader2 size={16} className="animate-spin mx-auto" /> : (t?.createPlaylist || 'Creer')}
        </button>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────
   ADD TO PLAYLIST MODAL (bottom sheet)
   ────────────────────────────────────────────── */
const AddToPlaylistModal = ({ open, onClose, playlists, onSelect, onCreateNew, trackName, t }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="add-to-playlist-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-md rounded-t-2xl px-5 pt-5 pb-8 animate-slide-up"
        style={{ background: '#0f0a1e', border: '1px solid rgba(255,255,255,0.08)', maxHeight: '60vh', overflowY: 'auto' }}
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white text-base font-medium">{t?.addToPlaylist || 'Ajouter a une playlist'}</h3>
          <button onClick={onClose} className="text-white/40 p-1" data-testid="add-to-playlist-close">
            <X size={18} />
          </button>
        </div>
        {trackName && (
          <p className="text-white/40 text-xs mb-4 truncate">{trackName}</p>
        )}

        <div className="space-y-1.5">
          {playlists.map((pl) => (
            <button
              key={pl.id}
              onClick={() => onSelect(pl)}
              className="w-full flex items-center gap-3 p-3 rounded-xl transition-colors active:scale-[0.98]"
              style={{ background: 'rgba(255,255,255,0.04)' }}
              data-testid={`add-to-playlist-option-${pl.id}`}
            >
              <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                <ListMusic size={13} className="text-indigo-400" />
              </div>
              <div className="text-left min-w-0 flex-1">
                <p className="text-white text-sm truncate">{pl.name}</p>
                <p className="text-white/35 text-xs">{pl.sounds?.length || 0} {t?.tracks || 'pistes'}</p>
              </div>
            </button>
          ))}

          {/* + New playlist */}
          <button
            onClick={onCreateNew}
            className="w-full flex items-center gap-3 p-3 rounded-xl transition-colors active:scale-[0.98]"
            style={{ background: 'rgba(139,92,246,0.08)', border: '1px dashed rgba(139,92,246,0.25)' }}
            data-testid="add-to-playlist-new"
          >
            <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0">
              <Plus size={14} className="text-purple-400" />
            </div>
            <p className="text-purple-300/80 text-sm">{t?.createNewPlaylist || 'Nouvelle playlist'}</p>
          </button>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────
   TOAST (lightweight success feedback)
   ────────────────────────────────────────────── */
const MiniToast = ({ message, visible }) => {
  if (!visible) return null;
  return (
    <div
      className="fixed top-16 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm animate-fade-in"
      style={{ background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7' }}
      data-testid="favorites-toast"
    >
      <Check size={14} />
      {message}
    </div>
  );
};

/* ──────────────────────────────────────────────
   DELETE PLAYLIST CONFIRM MODAL
   ────────────────────────────────────────────── */
const DeletePlaylistModal = ({ open, playlist, onConfirm, onClose, deleting, t }) => {
  if (!open || !playlist) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="delete-playlist-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-sm rounded-2xl p-5 animate-fade-in"
        style={{ background: '#0f0a1e', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <h3 className="text-white text-base font-medium mb-2">
          {t?.deletePlaylistTitle || 'Supprimer la playlist ?'}
        </h3>
        <p className="text-white/45 text-sm mb-5">
          <span className="text-white/70 font-medium">{playlist.name}</span>
          {' — '}{playlist.sounds?.length || 0} {t?.tracks || 'pistes'}
        </p>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm text-white/50 transition-colors active:scale-[0.97]"
            style={{ background: 'rgba(255,255,255,0.05)' }}
            data-testid="delete-playlist-cancel"
          >
            {t?.cancel || 'Annuler'}
          </button>
          <button
            onClick={() => onConfirm(playlist)}
            disabled={deleting}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors active:scale-[0.97]"
            style={{ background: 'rgba(239,68,68,0.2)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.25)' }}
            data-testid="delete-playlist-confirm"
          >
            {deleting ? <Loader2 size={14} className="animate-spin mx-auto" /> : (t?.delete || 'Supprimer')}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────
   PLAYLIST DETAIL SUB-VIEW
   ────────────────────────────────────────────── */
const PlaylistDetail = ({ playlist, onBack, onPlayTrack, onRemoveTrack, t }) => {
  const tracks = playlist?.sounds || [];

  return (
    <div data-testid="playlist-detail-view">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-white/50 text-sm mb-4 active:text-white/80 transition-colors"
        data-testid="playlist-detail-back"
      >
        <ChevronLeft size={16} />
        {t?.back || 'Retour'}
      </button>

      <div className="flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-xl bg-indigo-500/12 flex items-center justify-center">
          <ListMusic size={20} className="text-indigo-400" />
        </div>
        <div>
          <h2 className="text-white text-lg font-semibold">{playlist.name}</h2>
          <p className="text-white/40 text-xs">{tracks.length} {t?.tracks || 'pistes'}</p>
        </div>
      </div>

      {tracks.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Music size={24} className="text-white/15 mx-auto mb-2" />
          <p className="text-white/40 text-sm">{t?.noFavorites || 'Aucune piste'}</p>
        </div>
      ) : (
        <div>
          <button
            onClick={() => onPlayTrack(tracks[0], playlist)}
            className="w-full mb-4 h-11 rounded-2xl bg-gradient-to-r from-[#3B48EE] to-[#6F42EA] flex items-center justify-center gap-2 text-white font-medium shadow-[0_0_24px_rgba(90,90,255,0.35)] active:scale-[0.98] transition-transform"
          >
            <Play size={16} />
            <span>{t?.listen || 'Écouter'}</span>
          </button>

          <div className="space-y-1.5">
            {tracks.map((sound, i) => {
              const name = sound.display_name || sound.name || sound.path?.split('/').pop()?.replace(/\.[^.]+$/, '') || `Track ${i + 1}`;

              return (
                <div
                  key={`${i}-${sound.path}`}
                  className="flex items-center gap-2 p-3 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                  data-testid={`playlist-track-${i}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm text-white/50 font-medium">{i + 1}</span>
                  </div>

                  <div className="text-left min-w-0 flex-1">
                    <p className="text-white text-sm truncate">{name}</p>
                    {sound.category && (
                      <p className="text-white/40 text-xs truncate">
                        {getThemeLabel(sound.category, t)}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => onRemoveTrack(sound)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
                    style={{ background: 'rgba(239,68,68,0.08)' }}
                    data-testid={`playlist-track-remove-${i}`}
                  >
                    <Trash2 size={13} className="text-red-400/50" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────
   MAIN FAVORITES COMPONENT
   ────────────────────────────────────────────── */
const Favorites = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sub-view state
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [addToPlaylistTrack, setAddToPlaylistTrack] = useState(null); // sound object or null
  const [deleteTarget, setDeleteTarget] = useState(null); // playlist to delete
  const [deleting, setDeleting] = useState(false);

  // Toast
  const [toast, setToast] = useState({ visible: false, message: '' });

  // Pending create-then-add flow
  const [pendingTrackForNewPlaylist, setPendingTrackForNewPlaylist] = useState(null);

  const showToast = useCallback((msg) => {
    setToast({ visible: true, message: msg });
    setTimeout(() => setToast({ visible: false, message: '' }), 2200);
  }, []);

  /* ── Load data ── */
  useEffect(() => {
    const load = async () => {
      try {
        const [favsRes, playRes] = await Promise.all([
          api.getFavorites().catch(() => []),
          api.getPlaylists().catch(() => []),
        ]);
        setFavorites(Array.isArray(favsRes) ? favsRes : favsRes?.data || []);
        setPlaylists(Array.isArray(playRes) ? playRes : playRes?.data || []);
      } catch (e) {
        console.warn('[Favorites] load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* ── Actions ── */
  const playFavorite = (fav) => {
    const sound = fav.sound || fav;
    navigate(`/therapy?theme=favorites&track=${encodeURIComponent(sound.path || sound.audio_path || '')}`);
  };

  const playPlaylistTrack = (sound, playlist) => {
    navigate(`/therapy?playlist=${playlist.id}&track=${encodeURIComponent(sound.path || '')}`);
  };

  const handleCreatePlaylist = async (name) => {
    try {
      const newPl = await api.createPlaylist(name);
      setPlaylists((prev) => [...prev, newPl]);
      showToast(t?.playlistCreatedSuccess || 'Playlist creee');

      // If there's a pending track to add after creation
      if (pendingTrackForNewPlaylist && newPl?.id) {
        try {
          await api.addSoundToPlaylist(newPl.id, pendingTrackForNewPlaylist);
          // Update local state
          setPlaylists((prev) =>
            prev.map((pl) =>
              pl.id === newPl.id
                ? { ...pl, sounds: [...(pl.sounds || []), pendingTrackForNewPlaylist] }
                : pl
            )
          );
          showToast(t?.soundAddedToPlaylist || 'Son ajoute');
        } catch (addErr) {
          console.warn('[AddToPlaylist] error adding after create:', addErr);
        }
        setPendingTrackForNewPlaylist(null);
      }
    } catch (e) {
      if (e?.type === 'LIMIT_REACHED') {
        showToast(e.message);
      }
      throw e;
    }
  };

  const handleAddToPlaylist = async (playlist, track) => {
    try {
      const sound = track.sound || track;
      const soundRef = {
        path: sound.path || sound.audio_path || '',
        name: sound.display_name || sound.name || 'Unknown',
        type: sound.type || 'library',
        category: sound.category || sound.theme || null,
      };
      await api.addSoundToPlaylist(playlist.id, soundRef);
      // Update local state
      setPlaylists((prev) =>
        prev.map((pl) =>
          pl.id === playlist.id
            ? { ...pl, sounds: [...(pl.sounds || []), soundRef] }
            : pl
        )
      );
      setAddToPlaylistTrack(null);
      showToast(t?.soundAddedToPlaylist || 'Son ajoute');
    } catch (e) {
      console.warn('[AddToPlaylist] error:', e);
    }
  };

  const handleCreateNewFromAddSheet = () => {
    // Save the track, close add-sheet, open create modal
    if (addToPlaylistTrack) {
      const sound = addToPlaylistTrack.sound || addToPlaylistTrack;
      setPendingTrackForNewPlaylist({
        path: sound.path || sound.audio_path || '',
        name: sound.display_name || sound.name || 'Unknown',
        type: sound.type || 'library',
        category: sound.category || sound.theme || null,
      });
    }
    setAddToPlaylistTrack(null);
    setShowCreateModal(true);
  };

  const handleDeletePlaylist = async (playlist) => {
    setDeleting(true);
    try {
      await api.deletePlaylist(playlist.id);
      setPlaylists((prev) => prev.filter((pl) => pl.id !== playlist.id));
      setDeleteTarget(null);
      // If we were viewing this playlist's detail, go back
      if (selectedPlaylist?.id === playlist.id) setSelectedPlaylist(null);
      showToast(t?.playlistDeleted || 'Playlist supprimee');
    } catch (e) {
      console.warn('[DeletePlaylist] error:', e);
    } finally {
      setDeleting(false);
    }
  };

  const handleRemoveTrackFromPlaylist = async (sound) => {
    if (!selectedPlaylist) return;
    try {
      await api.removeSoundFromPlaylist(selectedPlaylist.id, sound.path);
      const updatedSounds = (selectedPlaylist.sounds || []).filter((s) => s.path !== sound.path);
      const updated = { ...selectedPlaylist, sounds: updatedSounds };
      setSelectedPlaylist(updated);
      setPlaylists((prev) => prev.map((pl) => (pl.id === updated.id ? updated : pl)));
      showToast(t?.trackRemoved || 'Piste retiree');
    } catch (e) {
      console.warn('[RemoveTrack] error:', e);
    }
  };

  const handleRemoveFavorite = async (fav) => {
  try {
    const sound = fav.sound || fav;
    const soundPath = sound.path || sound.audio_path || '';
    const soundName =
      sound.display_name ||
      sound.name ||
      sound.path?.split('/').pop()?.replace(/\.[^.]+$/, '') ||
      'ce favori';

    if (!soundPath) return;

    const confirmed = window.confirm(
      t?.locale === 'fr-FR'
        ? `Retirer "${soundName}" des favoris ?`
        : `Remove "${soundName}" from favorites?`
    );

    if (!confirmed) return;

    await api.removeFavorite(soundPath);

    setFavorites((prev) =>
      prev.filter((item) => {
        const itemSound = item.sound || item;
        const itemPath = itemSound.path || itemSound.audio_path || '';
        return itemPath !== soundPath;
      })
    );

    showToast(t?.removeFromFavorites || 'Retire des favoris');
  } catch (e) {
    console.warn('[RemoveFavorite] error:', e);
  }
};

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#030014' }}>
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  /* ── Playlist detail sub-view ── */
  if (selectedPlaylist) {
    return (
      <div
       className="min-h-screen pt-14 px-5"
        style={{
          background: '#030014',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)',
        }}
        data-testid="favorites-screen"
      >
        <PlaylistDetail
          playlist={selectedPlaylist}
          onBack={() => setSelectedPlaylist(null)}
          onPlayTrack={playPlaylistTrack}
          onRemoveTrack={handleRemoveTrackFromPlaylist}
          t={t}
        />
      </div>
    );
  }

  /* ── Main view ── */
  return (
    <div
      className="min-h-screen pt-14 px-5"
      style={{
        background: '#030014',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)',
      }}
      data-testid="favorites-screen"
    >
      <MiniToast message={toast.message} visible={toast.visible} />

      {/* ── Section: Playlists ── */}
      <section className="mb-7" data-testid="playlists-section">
        <h2 className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-3">
          {t?.playlistsLabel || 'Playlists'}
        </h2>

        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide" data-testid="playlists-row">
          {playlists.map((pl) => (
            <div
              key={pl.id}
              className="flex-shrink-0 w-36 rounded-2xl p-4 transition-all relative"
              style={{
                background: 'rgba(99,102,241,0.07)',
                border: '1px solid rgba(99,102,241,0.15)',
              }}
              data-testid={`playlist-card-${pl.id}`}
            >
              {/* Delete button */}
              <button
                onClick={(e) => { e.stopPropagation(); setDeleteTarget(pl); }}
                className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full flex items-center justify-center transition-colors active:scale-90"
                style={{ background: 'rgba(255,255,255,0.06)' }}
                data-testid={`playlist-delete-${pl.id}`}
              >
                <Trash2 size={11} className="text-white/30" />
              </button>
              {/* Card content (clickable) */}
              <button
                onClick={() => setSelectedPlaylist(pl)}
                className="w-full text-left active:scale-[0.96] transition-transform"
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-500/12 flex items-center justify-center mb-3">
                  <ListMusic size={18} className="text-indigo-400" />
                </div>
                <p className="text-white text-sm font-medium truncate">{pl.name}</p>
                <p className="text-white/35 text-xs mt-0.5">{pl.sounds?.length || 0} {t?.tracks || 'pistes'}</p>
              </button>
            </div>
          ))}

          {/* + New playlist card */}
          <button
            onClick={() => { setPendingTrackForNewPlaylist(null); setShowCreateModal(true); }}
            className="flex-shrink-0 w-36 rounded-2xl p-4 transition-all active:scale-[0.96] flex flex-col items-center justify-center"
            style={{
              background: 'rgba(139,92,246,0.05)',
              border: '1px dashed rgba(139,92,246,0.25)',
              minHeight: '120px',
            }}
            data-testid="new-playlist-card"
          >
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center mb-2">
              <Plus size={18} className="text-purple-400" />
            </div>
            <p className="text-purple-300/60 text-xs font-medium">{t?.createNewPlaylist || 'Nouvelle playlist'}</p>
          </button>
        </div>
      </section>

      {/* ── Section: Favorites ── */}
      <section data-testid="favorites-section">
        <h2 className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-3">
          {t?.favoritesLabel || 'Favoris'}
        </h2>

        {favorites.length === 0 ? (
          <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Heart size={28} className="text-white/15 mx-auto mb-3" />
            <p className="text-white/40 text-sm">{t?.noFavorites || 'Aucun favori pour le moment'}</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {favorites.map((fav, i) => {
              const sound = fav.sound || fav;
              const name = sound.display_name || sound.name || sound.path?.split('/').pop()?.replace(/\.[^.]+$/, '') || `Track ${i + 1}`;
              const theme = sound.category || sound.theme || '';
              return (
                <div
                  key={sound.path || fav.id || i}
                  className="flex items-center gap-2 p-3 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                  data-testid={`favorite-item-${i}`}
                >
                  {/* Play button */}
                  <button
                    onClick={() => playFavorite(fav)}
                    className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform"
                    data-testid={`favorite-play-${i}`}
                  >
                    <Play size={14} className="text-purple-400 ml-0.5" />
                  </button>

                  {/* Track info */}
                  <button
                    onClick={() => playFavorite(fav)}
                    className="text-left min-w-0 flex-1"
                  >
                    <p className="text-white text-sm truncate">{name}</p>
                    {theme && <p className="text-white/40 text-xs truncate">{getThemeLabel(theme, t)}</p>}
                  </button>

                  {/* Remove favorite button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFavorite(fav);
                    }}
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
                    style={{ background: 'rgba(239,68,68,0.08)' }}
                    data-testid={`favorite-remove-${i}`}
                    title={t?.removeFromFavorites || 'Retirer des favoris'}
                  >
                    <Trash2 size={13} className="text-red-400/60" />
                    </button>
                  {/* Add to playlist button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setAddToPlaylistTrack(fav); }}
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform"
                    style={{ background: 'rgba(139,92,246,0.08)' }}
                    data-testid={`favorite-add-to-playlist-${i}`}
                    title={t?.addToPlaylist || 'Ajouter a une playlist'}
                  >
                    <Plus size={14} className="text-purple-300/70" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Modals ── */}
      <CreatePlaylistModal
        open={showCreateModal}
        onClose={() => { setShowCreateModal(false); setPendingTrackForNewPlaylist(null); }}
        onCreate={handleCreatePlaylist}
        t={t}
      />

      <AddToPlaylistModal
        open={!!addToPlaylistTrack}
        onClose={() => setAddToPlaylistTrack(null)}
        playlists={playlists}
        onSelect={(pl) => handleAddToPlaylist(pl, addToPlaylistTrack)}
        onCreateNew={handleCreateNewFromAddSheet}
        trackName={(addToPlaylistTrack?.sound || addToPlaylistTrack)?.display_name || (addToPlaylistTrack?.sound || addToPlaylistTrack)?.name}
        t={t}
      />

      <DeletePlaylistModal
        open={!!deleteTarget}
        playlist={deleteTarget}
        onConfirm={handleDeletePlaylist}
        onClose={() => setDeleteTarget(null)}
        deleting={deleting}
        t={t}
      />
    </div>
  );
};

export default Favorites;
